import {
  SortTypeEnum,
  UserRole,
  UserService,
  UserStatus,
  VehicleAccidentPaymentStatusEnum,
  VehicleAccidentRepairStatusEnum,
  VehicleAccidentService
} from 'api/generated';
import { Link, defaultItemsPerPage } from 'components/table/types';
import { JobTitlesModel, QueryError } from 'features/shared/model';
import { UseQueryOptions, useQuery } from 'react-query';
import { createLinks } from 'utils/pagination';

export const useEmployeesQueryKey = 'EmployeesQuery';
export const useVehicleAccidentQueryKey = 'VehicleAccidentQuery';

export type FetchEmployeesInput = {
  role?: UserRole[];
  branchId?: number;
  departmentId?: number;
  status?: Array<UserStatus>;
  furlough?: boolean;
  isEmployee?: boolean;
  name?: string;
  canBeApprover?: boolean;
  jobTitle?: JobTitlesModel;
  minEmploymentEndDateInclude?: string;
  hasPermissionDriveCompanyCar?: boolean;
  sort?: string;
  order?: SortTypeEnum;
  skipPagination?: boolean;
  page?: number;
  maxCount?: number;
};

const fetchEmployees = async (input: FetchEmployeesInput) => {
  const data = await UserService.getUsers(
    input.role,
    input.branchId,
    input.departmentId,
    input.status,
    input.furlough,
    input.isEmployee,
    input.name,
    input.canBeApprover,
    input.minEmploymentEndDateInclude,
    input.jobTitle,
    input.hasPermissionDriveCompanyCar,
    input.sort,
    input.order,
    input.skipPagination,
    input.page,
    input.maxCount
  );
  const employees = data.users || [];
  const pages = Math.ceil((data.count || 0) / defaultItemsPerPage);

  const links: Array<Link> = createLinks(pages, input.page);

  return {
    data: employees,
    totalCount: data.count,
    payload: { pagination: { page: input.page || 1, links, items_per_page: defaultItemsPerPage } }
  };
};

export const useEmployeesQuery = <TQueryKey extends Array<unknown> = unknown[]>(
  input: FetchEmployeesInput,
  queryKey?: TQueryKey,
  options?: Omit<
    UseQueryOptions<
      Awaited<ReturnType<typeof fetchEmployees>>,
      QueryError,
      Awaited<ReturnType<typeof fetchEmployees>>,
      unknown[]
    >,
    'queryKey' | 'queryFn' | 'initialData'
  >
) => useQuery([useEmployeesQueryKey, ...(queryKey ?? [{ ...input }])], () => fetchEmployees(input), options);

export type FetchVehicleAccidentsInput = {
  ids?: Array<number>;
  dateFrom?: string;
  dateTo?: string;
  regNStock?: string;
  managerId?: number;
  sort?: string;
  order?: SortTypeEnum;
  paymentStatus?: VehicleAccidentPaymentStatusEnum;
  repairStatus?: VehicleAccidentRepairStatusEnum;
  isClose?: boolean;
  skipPagination?: boolean;
  page?: number;
  maxCount?: number;
};

const fetchVehicleAccidents = async (input: FetchVehicleAccidentsInput) => {
  const data = await VehicleAccidentService.getVehicleAccident(
    input.ids,
    input.dateFrom,
    input.dateTo,
    input.regNStock,
    input.managerId,
    input.sort,
    input.order,
    input.paymentStatus,
    input.repairStatus,
    input.isClose,
    input.skipPagination,
    input.page,
    input.maxCount
  );
  const pages = Math.ceil((data.count || 0) / defaultItemsPerPage);

  const links: Array<Link> = createLinks(pages, input.page);

  return {
    data: data.items,
    totalCount: data.count,
    payload: { pagination: { page: input.page || 1, links, items_per_page: defaultItemsPerPage } }
  };
};

export const useVehicleAccidentQuery = <TQueryKey extends Array<unknown> = unknown[]>(
  input: FetchVehicleAccidentsInput,
  queryKey?: TQueryKey,
  options?: Omit<
    UseQueryOptions<
      Awaited<ReturnType<typeof fetchVehicleAccidents>>,
      QueryError,
      Awaited<ReturnType<typeof fetchVehicleAccidents>>,
      unknown[]
    >,
    'queryKey' | 'queryFn' | 'initialData'
  >
) =>
  useQuery([useVehicleAccidentQueryKey, ...(queryKey ?? [{ ...input }])], () => fetchVehicleAccidents(input), options);
